import { useEffect, useState, useRef, KeyboardEvent } from "react";
import { observer } from "mobx-react";
import { Modal, Form, Input, Radio } from "antd";
import { useTranslation } from "react-i18next";
import SJButton from "@/components/SJButton";
import cls from "classnames";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { PHONE_PATTERNS, REGIONS } from "@/store/const";
import PolicyDialog from "@/components/PolicyDialog";
import CountDownButton from "logic/src/components/CountDownButton";
import PasswordEye from "@/components/PasswordEye";
import useTrackCLK from "logic/src/utils/useTrackCLK";
import { validatePassword } from "logic/src/utils";
import useMessage from "logic/src/utils/useMessage";
import { AUTH_ERROR_TIP, LOGIN_TYPE } from "logic/src/constants/user";
import authVM from "logic/src/businesses/auth/vm";
import styles from "../../index.module.scss";

const ResetPassword: React.FC<{
  onFinishResetPassword: () => void;
  onGotoLogin: () => void;
}> = ({ onFinishResetPassword, onGotoLogin }) => {
  const [form] = Form.useForm();
  const mobile = Form.useWatch("mobile", form);
  const sms_code = Form.useWatch("sms_code", form);
  const password = Form.useWatch("password", form);
  const confirm = Form.useWatch("confirm", form);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useMessage();
  const { sendCLK } = useTrackCLK();
  const showToast = (text: string) => {
    message.warning(text);
  };

  const handleResetPassword = async () => {
    try {
      const checkSuccess = await form
        .validateFields(["mobile", "sms_code", "password", "confirm"])
        .then(() => true)
        .catch(() => false);
      if (!checkSuccess) return;
      await authVM.resetPassword({
        mobile,
        sms_code,
        password,
        confirm,
      });
      message.success({
        type: "success",
        content: t("sensechat-295"), // 操作成功
      });
      onFinishResetPassword();
    } catch (error: any) {
      if (error && error.code) {
        showToast(AUTH_ERROR_TIP[error.code] || "请求失败");
      } else {
        throw error;
      }
    }
  };

  const handleGetVerifyCode = async () => {
    try {
      sendCLK("login_code_click");
      const checkSuccess = await form
        .validateFields(["mobile"])
        .then(() => true)
        .catch(() => false);
      if (!checkSuccess) return;
      await authVM.getVerifyCode(mobile);
    } catch (error: any) {
      if (error && error.code) {
        showToast(AUTH_ERROR_TIP[error.code] || "发送失败");
      } else {
        throw error;
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      handleResetPassword();
    }
  };

  return (
    <>
      <p className={styles.forgetText}>忘记密码</p>
      <Form
        form={form}
        layout="horizontal"
        name="reset"
        className={styles["new-login-form"]}
      >
        <Form.Item
          name="mobile"
          className={styles["new-login-form-cell"]}
          getValueFromEvent={event => {
            return event.target.value.replace(/\s+/g, "");
          }}
          validateTrigger="onConfirm"
          rules={[
            {
              required: true,
              message: t("sensechat-81") as string, // "请输入手机号"
            },
            {
              pattern: PHONE_PATTERNS[REGIONS.mainland],
              whitespace: true,
              message: t("sensechat-73") as string, // "请输入正确的手机号码"
            },
          ]}
        >
          <Input
            placeholder={t("sensechat-63") as string} // "请输入手机号"
            maxLength={11}
            className="large-input"
            autoComplete="off"
            onKeyDown={handleKeyDown}
            type="number"
          />
        </Form.Item>
        <Form.Item
          className={styles["new-login-form-cell"]}
          name="sms_code"
          validateTrigger="onConfirm"
          getValueFromEvent={event => {
            return event.target.value.replace(/\s+/g, "");
          }}
          rules={[
            {
              required: true,
              message: t("sensechat-74") as string, // "请输入短信验证码"
            },
            {
              max: 6,
              min: 6,
              whitespace: true,
              message: t("sensechat-100") as string, // "请输入合法的短信验证码"
            },
          ]}
        >
          <Input
            className="large-input"
            placeholder={t("sensechat-64") as string} // "短信验证码"
            maxLength={6}
            type="number"
            autoComplete="off"
            onKeyDown={handleKeyDown}
            suffix={
              <CountDownButton
                className="verify-code"
                onSendCode={handleGetVerifyCode}
                waiting={authVM.smsCode.waiting}
                count={authVM.smsCode.count}
              >
                {/* 发送验证码 */}
                {t("sensechat-93")}
              </CountDownButton>
            }
          />
        </Form.Item>
        <Form.Item
          className={styles["new-login-form-cell"]}
          name="password"
          validateFirst
          validateTrigger="onConfirm"
          rules={[
            {
              required: true,
              message: t("sensechat-276") as string, // "请输入密码"
            },
            {
              required: true,
              min: 6,
              message: t("sensechat-277") as string, // "密码至少6位"
            },
            {
              validator: (_: any, value: string) => {
                if (!validatePassword(value)) {
                  return Promise.reject(
                    new Error(t("sensechat-278") as string),
                  ); // 密码必须同时包含数字及大小写字母
                } else {
                  return Promise.resolve();
                }
              },
            },
          ]}
        >
          <Input.Password
            className="large-input"
            placeholder={t("sensechat-82") as string} // 请输入密码
            iconRender={PasswordEye}
            autoComplete="off"
            maxLength={100}
            onKeyDown={handleKeyDown}
          />
        </Form.Item>
        <Form.Item
          className={styles["new-login-form-cell"]}
          name="confirm"
          validateFirst
          validateTrigger="onConfirm"
          rules={[
            {
              required: true,
              message: t("sensechat-95") as string, // "请再次输入密码"
            },
            {
              required: true,
              min: 6,
              message: t("sensechat-277") as string, // "密码至少6位"
            },
            {
              validator: (_: any, value: string) => {
                if (!validatePassword(value)) {
                  return Promise.reject(
                    new Error(t("sensechat-278") as string),
                  ); // 密码必须同时包含数字及大小写字母
                } else {
                  return Promise.resolve();
                }
              },
            },
            {
              validator(_, value) {
                if (password !== value) {
                  return Promise.reject(
                    new Error(t("sensechat-281") as string),
                  ); // 新密码与确认新密码不同
                } else {
                  return Promise.resolve();
                }
              },
            },
          ]}
        >
          <Input.Password
            className="large-input"
            placeholder={t("sensechat-280") as string} // 请再次输入密码
            autoComplete="off"
            iconRender={PasswordEye}
            maxLength={100}
            onKeyDown={handleKeyDown}
          />
        </Form.Item>
        <SJButton
          block
          size="large"
          type="primary"
          onClick={handleResetPassword}
          className={styles["new-login-button"]}
        >
          确定
        </SJButton>
        <p className={styles.tip} onClick={onGotoLogin}>
          返回登录
        </p>
      </Form>
      {message.contextHolder}
    </>
  );
};

export default observer(ResetPassword);
